<template>
  <div>
    <div class="container">
      <a href="/home?view=TeamDashboard" class="btn btn-default-back back-button"> My Team </a>
      <PathwayWheelHeader :currentOutcome="currentOutcome" />
    </div>
    <div class="cms-session-wheel">
      <SessionWheel
        :model="model"
        :wheel-colour="wheelColour"
        :includeBackButton="true"
        v-if="model"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import axios from "axios";
import { computed, ref, triggerRef, watch } from "vue";
import { csContext } from "../../services/cs-context";
import {
  CmsGoalData,
  CmsTeamPathway,
  CmsTeamPathwayStep,
} from "../../services/cms/cms-type-definitions";
import cms from "../../services/cms/cms";
import { TeamMemberCompletedPathwayStepApiModel } from "../../api-models";
import SessionWheel, { CmsSessionApiModel } from "../sessions/sessions-wheel.vue";
import PathwayWheelHeader from "../../components/cms/pathway-wheel-header.vue";

const props = defineProps<{ doc: CmsTeamPathway }>();

const outcome = ref<Partial<CmsGoalData> | null>(null);
// undefined means pending, null means that they have not completed the step.
const memberProgress = ref<(TeamMemberCompletedPathwayStepApiModel | null | undefined)[]>([]);

const wheelColour = computed(
  () => props.doc.data?.goal_relationship.data?.goal_colour || "#B0BB36",
);
const teamId = computed(() => csContext.currentTeam?.id ?? "");
const currentOutcome = computed(() => props.doc.data.goal_relationship.data ?? null);

function updateCompletion(): void {
  if (teamId.value) {
    const tempMemberProgress = props.doc.data.pathway_steps.map(
      () => undefined as TeamMemberCompletedPathwayStepApiModel | null | undefined,
    );

    memberProgress.value = tempMemberProgress;
    props.doc.data.pathway_steps.forEach((step, index) =>
      axios
        .get(
          `/api/TeamMemberCompletedPathwayStep?cmsStepId=${step.pathway_step.id}&cmsPathwayId=${props.doc.id}&teamId=${teamId.value}`,
        )
        .then((value) => {
          memberProgress.value[index] = value.data;
          triggerRef(memberProgress);
        }),
    );
  } else {
    memberProgress.value = props.doc.data.pathway_steps.map(
      () => null as TeamMemberCompletedPathwayStepApiModel | null | undefined,
    );
  }
}

watch(
  () => props.doc,
  () => {
    updateCompletion();
  },
  { immediate: true },
);

const model = computed(() => {
  // If any are undefined, we're still loading.
  if (!memberProgress.value?.every((p) => p !== undefined)) {
    return null;
  }

  const sessions = props.doc.data.pathway_steps.map((step, index): CmsSessionApiModel => {
    const pathwayStep: CmsTeamPathwayStep = step.pathway_step as any;
    const isCompleted = !!memberProgress.value[index];

    return {
      id: pathwayStep.id,
      index,
      shortTitle: cms.asText(pathwayStep.data?.tile_title) || cms.asText(pathwayStep.data.title),
      fullTitle: cms.asText(pathwayStep.data.title),
      availableDescription: [],
      unavailableDescription: [],
      theme: "team-theme",
      availableDescriptionFromCms: step.step_description,
      unavailableDescriptionFromCms: step.step_description,
      durationDescription: "",
      callToAction: "",
      isAvailable: true,
      isCompleted,
      latestSceneIndex: 0,
      imageUrl: props.doc.data?.title_image?.url || pathwayStep.data.title_image.url,
      url: cms.linkResolver(pathwayStep),
      buttonText: cms.asText(step.step_button_text) || "Go",
      itemTypeText: cms.asText(step.step_type_text) || "Step",
    };
  });
  return { sessions };
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="less">
@import "../../styles/outcomes/pathway-wheel";
</style>
